import { useCallback, useRef } from 'react'

/**
 * Where focus goes back to when a dialog or drawer closes.
 *
 * Radix returns focus to whatever was focused when the dialog opened. A dialog opened from a menu
 * item (More › Archive, a row's ⋯ › Archive) opens as that menu closes, and the item is gone by
 * then: focus fell to <body> and a keyboard user had to start again from the top of the page. So
 * every menu remembers its trigger while it is open (and just after), and a dialog opened from one
 * returns focus to that trigger: the page's More button or the row's ⋯ button.
 */
let lastMenu: { trigger: HTMLElement | null; open: boolean; closedAt: number } | null = null

/** How long after a menu closes a dialog opening still counts as opened from it. */
const FROM_MENU_MS = 1000

/** The ref of a menu's content element: set when the menu opens, cleared when it closes. */
export function trackMenu(content: HTMLElement | null): void {
  if (content) {
    const id = content.getAttribute('aria-labelledby')
    lastMenu = { trigger: id ? document.getElementById(id) : null, open: true, closedAt: 0 }
  } else if (lastMenu?.open) {
    lastMenu = { ...lastMenu, open: false, closedAt: Date.now() }
  }
}

function focusTarget(): HTMLElement | null {
  const active = document.activeElement
  const onPage = active instanceof HTMLElement && active !== document.body
  const fromMenu =
    lastMenu !== null &&
    (lastMenu.open || Date.now() - lastMenu.closedAt < FROM_MENU_MS) &&
    (!onPage || active.closest('[role="menu"]') !== null)
  if (fromMenu) return lastMenu?.trigger ?? null
  return onPage ? active : null
}

/**
 * For a dialog's content element: `ref` notes, as the dialog opens, where focus should return;
 * `onCloseAutoFocus` returns it there. When that element is gone (the row was archived out of the
 * list), focus goes to the table's current row, and otherwise Radix does what it does.
 */
export function useReturnFocus() {
  const target = useRef<HTMLElement | null>(null)
  const ref = useCallback((content: HTMLElement | null) => {
    if (content) target.current = focusTarget()
  }, [])
  const onCloseAutoFocus = useCallback((event: Event) => {
    const element = target.current
    target.current = null
    const next = element?.isConnected
      ? element
      : document.querySelector<HTMLElement>('[data-row-index][tabindex="0"]')
    if (!next) return
    event.preventDefault()
    next.focus()
  }, [])
  return { ref, onCloseAutoFocus }
}
